<script setup lang="ts">
const props = withDefaults(defineProps<{
  language?: string
  base_url?: string
  footerType?: 'auth' | 'default'
}>(), {
  language: 'en',
  footerType: 'default',
})

const config = useRuntimeConfig();
const baseUrl = computed(() => props.base_url ?? config.public.wadPlatformBaseUrl);

// TAL-10764: the 'JOBS & CAREER' column is host-aware, not gone.
//
// Jobs, Companies, Discover, WeAreDevelopers LIVE, Podcast and Magazine are the
// public developer surfaces. They have no place on the companies.* portal, but
// wad-platform keeps serving public pages on www after the Labs cutover, so on
// www this footer must stay exactly as it is today.
//
// Newsletter belongs to this column on www. It is the one entry that still makes
// sense on the portal, so on companies.* it moves into 'ABOUT US' rather than
// being left alone under a heading that no longer describes it.
//
// useIsCompaniesHost() rather than baseUrl: baseUrl is wadPlatformBaseUrl (a
// single env value, always the www origin) and is what these links are built
// from, so it cannot also answer "which host am I rendering on". Unlike the menu,
// this component is Nuxt-only - it is not part of the menu custom element - so the
// Nuxt composable is available here.
const isCompaniesHost = useIsCompaniesHost();

const newsletterItem: FooterItem = { text: 'Newsletter', url: 'https://www.wearedevelopers.com/business/newsletter' };

const jobsAndCareerColumn: FooterColumn = {
  header: 'JOBS & CAREER',
  items: [
    { text: 'Jobs', url: `${baseUrl.value}/jobs` },
    { text: 'Companies', url: `${baseUrl.value}/companies` },
    { text: 'Discover', url: `${baseUrl.value}/videos` },
    { text: 'WeAreDevelopers LIVE', url: `${baseUrl.value}/live`},
    { text: 'Podcast', url: `${baseUrl.value}/videos?event=coffee-with-developers`},
    { text: 'Magazine', url: `${baseUrl.value}/magazine` },
    newsletterItem,
  ]
}

const defaultFooterItems: FooterColumn[] = [
  {
    header: 'EVENTS',
    items: [
      { text: "World Congress Europe '26", url: 'https://www.wearedevelopers.com/world-congress/'},
      { text: "Tech Leaders Summit '26", url: 'https://www.wearedevelopers.com/world-congress/tech-leaders-summit'},
      { text: "HR Leaders Summit '26", url: 'https://www.wearedevelopers.com/world-congress/hr-leaders-summit'},
      { text: "World Congress North America '26", url: 'https://www.wearedevelopers.com/world-congress-north-america'},
      { text: "Conference India '26", url: 'https://www.wearedevelopers.com/conference-india'},
      { text: "Become a Sponsor", url: ' https://www.wearedevelopers.com/business/event-partnerships'}
    ]
  },
  {
    header: 'FOR BUSINESS',
    items: [
      { text: 'Employer Branding', url: `${baseUrl.value}/talent-marketing-membership` },
      { text: 'Tech Recruiting', url: `${baseUrl.value}/developer-recruiting-membership` },
      { text: 'Product Marketing', url: `${baseUrl.value}/product-marketing-membership` },
      { text: 'Event Partnerships', url: ` https://www.wearedevelopers.com/business/event-partnerships` },
      { text: 'Contact Sales', url: `https://www.wearedevelopers.com/business/book-a-call`}
    ]
  },
]

const social: FooterColumn = {
  items: [
    { text: 'YouTube', url: 'https://www.youtube.com/channel/UCSD0dLRGQk_T-D3RvpM5aFQ', icon: '<svg  id="icon-youtube" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M14.7072 5.40562C14.6281 5.1338 14.4721 4.8857 14.2549 4.68618C14.0376 4.48667 13.7667 4.34272 13.4693 4.26878C12.3779 4 8 4 8 4C8 4 3.62208 4 2.53008 4.26878C2.23282 4.34285 1.96209 4.48685 1.74496 4.68635C1.52784 4.88586 1.37193 5.13388 1.29283 5.40562C1 6.40834 1 8.5 1 8.5C1 8.5 1 10.5917 1.29283 11.5944C1.37193 11.8662 1.5279 12.1143 1.74514 12.3138C1.96239 12.5133 2.23327 12.6573 2.53067 12.7312C3.62208 13 8 13 8 13C8 13 12.3779 13 13.4699 12.7312C13.7673 12.6573 14.0383 12.5134 14.2555 12.3139C14.4728 12.1143 14.6287 11.8662 14.7078 11.5944C15 10.5917 15 8.5 15 8.5C15 8.5 15 6.40834 14.7072 5.40562ZM6.56792 10.399V6.60101L10.2272 8.5L6.56792 10.399Z" fill="currentColor"/></svg>' },
    { text: 'X', url: 'https://twitter.com/WeAreDevs', icon: '<svg id="icon-x" xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none"><path d="M0 0L4.73047 6.76042L0.248698 12H2.00911L5.51823 7.88672L8.39583 12H13.0026L8.05469 4.91667L12.2487 0H10.5156L7.26953 3.79167L4.62109 0H0ZM2.5599 1.33333H3.92578L10.444 10.6667H9.08984L2.5599 1.33333Z" fill="currentColor"/></svg>'},
    { text: 'LinkedIn', url: 'https://www.linkedin.com/company/wearedevelopers.org', icon: '<svg id="icon-linkedin" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12.9274 12.9303H10.8542V9.68175C10.8542 8.90708 10.8385 7.91017 9.77392 7.91017C8.693 7.91017 8.52792 8.75308 8.52792 9.62458V12.9303H6.45475V6.25H8.44625V7.16058H8.47308C8.75133 6.63558 9.428 6.08142 10.4389 6.08142C12.5395 6.08142 12.928 7.46392 12.928 9.2635L12.9274 12.9303ZM4.11325 5.33592C3.44592 5.33592 2.90983 4.79575 2.90983 4.13133C2.90983 3.4675 3.4465 2.92792 4.11325 2.92792C4.77825 2.92792 5.31725 3.4675 5.31725 4.13133C5.31725 4.79575 4.77767 5.33592 4.11325 5.33592ZM5.15275 12.9303H3.07375V6.25H5.15275V12.9303ZM13.9646 1H2.03308C1.462 1 1 1.4515 1 2.00858V13.9914C1 14.5491 1.462 15 2.03308 15H13.9628C14.5333 15 15 14.5491 15 13.9914V2.00858C15 1.4515 14.5333 1 13.9628 1H13.9646Z" fill="currentColor"/></svg>' },
    { text: 'Facebook', url: 'https://www.facebook.com/wearedevelopers', icon: '<svg id="icon-facebook" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M15 8.04255C15 4.1533 11.8657 1 8 1C4.13425 1 1 4.1533 1 8.04255C1 11.558 3.55967 14.4712 6.90625 14.9994V10.0784H5.12883V8.04196H6.90625V6.49143C6.90625 4.72668 7.95158 3.75129 9.5505 3.75129C10.3158 3.75129 11.1173 3.88921 11.1173 3.88921V5.62226H10.2342C9.36442 5.62226 9.09317 6.16512 9.09317 6.72207V8.04255H11.0345L10.7242 10.079H9.09317V15C12.4403 14.4712 15 11.5574 15 8.04255Z" fill="currentColor"/></svg>' },
    { text: 'Instagram', url: 'https://www.instagram.com/_wearedevelopers/', icon: '<svg id="icon-instagram" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 8C10 8.39556 9.8827 8.78224 9.66294 9.11114C9.44318 9.44004 9.13082 9.69638 8.76537 9.84776C8.39991 9.99913 7.99778 10.0387 7.60982 9.96157C7.22186 9.8844 6.86549 9.69392 6.58579 9.41421C6.30608 9.13451 6.1156 8.77814 6.03843 8.39018C5.96126 8.00222 6.00087 7.60009 6.15224 7.23463C6.30362 6.86918 6.55996 6.55682 6.88886 6.33706C7.21776 6.1173 7.60444 6 8 6C8.53025 6.00061 9.0386 6.21151 9.41354 6.58646C9.78848 6.9614 9.99939 7.46975 10 8ZM14.25 5.25V10.75C14.2489 11.6779 13.8799 12.5676 13.2237 13.2237C12.5676 13.8799 11.6779 14.2489 10.75 14.25H5.25C4.32206 14.2489 3.43244 13.8799 2.77629 13.2237C2.12014 12.5676 1.75105 11.6779 1.75 10.75V5.25C1.75105 4.32206 2.12014 3.43244 2.77629 2.77629C3.43244 2.12014 4.32206 1.75105 5.25 1.75H10.75C11.6779 1.75105 12.5676 2.12014 13.2237 2.77629C13.8799 3.43244 14.2489 4.32206 14.25 5.25ZM11 8C11 7.40666 10.8241 6.82664 10.4944 6.33329C10.1648 5.83994 9.69623 5.45542 9.14805 5.22836C8.59987 5.0013 7.99667 4.94189 7.41473 5.05764C6.83279 5.1734 6.29824 5.45912 5.87868 5.87868C5.45912 6.29824 5.1734 6.83279 5.05764 7.41473C4.94189 7.99667 5.0013 8.59987 5.22836 9.14805C5.45542 9.69623 5.83994 10.1648 6.33329 10.4944C6.82664 10.8241 7.40666 11 8 11C8.79537 10.9991 9.55791 10.6827 10.1203 10.1203C10.6827 9.55791 10.9991 8.79537 11 8ZM12 4.75C12 4.60166 11.956 4.45666 11.8736 4.33332C11.7912 4.20999 11.6741 4.11386 11.537 4.05709C11.4 4.00032 11.2492 3.98547 11.1037 4.01441C10.9582 4.04335 10.8246 4.11478 10.7197 4.21967C10.6148 4.32456 10.5434 4.4582 10.5144 4.60368C10.4855 4.74917 10.5003 4.89997 10.5571 5.03701C10.6139 5.17406 10.71 5.29119 10.8333 5.3736C10.9567 5.45601 11.1017 5.5 11.25 5.5C11.4489 5.5 11.6397 5.42098 11.7803 5.28033C11.921 5.13968 12 4.94891 12 4.75Z" fill="currentColor"/></svg>' },
  ]
}

const wad: FooterColumn = {
  header: 'ABOUT US',
  items: [
    { text: 'About WeAreDevelopers', url: 'https://www.wearedevelopers.com/company' },
    { text: 'Contact', url: 'https://www.wearedevelopers.com/company/contact' },
    { text: 'Press & Media', url: 'https://www.wearedevelopers.com/company/media' },
    // Newsletter's home is the JOBS & CAREER column; it only lands here on the
    // portal host, where that column is dropped.
    ...(isCompaniesHost ? [newsletterItem] : []),
  ]
}

// Absolute production URLs on purpose: the legal pages are a single canonical
// set served by the webflow-routes Worker, which only has /legal* on the
// production zone - dev and staging have no equivalent.
//
// /legal rather than the old /about/legal: today prod-spa 301s /about/legal to
// /legal, and that Worker route is deleted at the cutover, after which
// /about/legal falls through to Labs. webflow-routes is explicitly left as-is,
// so /legal keeps working. Linking the final URL survives the cutover untouched.
//
// The same reasoning was applied to the rest of this footer on 2026-08-07. Every
// link that answered with a 301/302 now names its final destination, measured
// against production:
//
//   /events/partners              -> /business/event-partnerships
//   /about                        -> /company
//   /about/contact-us             -> /company/contact
//   /about/newsletter             -> /business/newsletter
//   /about/press                  -> /company/media
//   /business/meeting-vereinbaren -> /business/book-a-call
//   /world-congress/tech-leaders  -> /world-congress/tech-leaders-summit
//
// All seven targets return 200 directly. This costs users a redirect each and,
// more importantly, stops the footer depending on a redirect layer that the
// cutover edits.
const authFooterItems: FooterColumn[] = [
  {
    items: [
      { text: 'Privacy & Terms', url: 'https://www.wearedevelopers.com/legal' },
      { text: 'Manage Cookies', url: 'javascript:Truendo.openCookieSettings()' },
      { text: 'Imprint', url: 'https://www.wearedevelopers.com/legal/imprint' }
    ]
  }
]

const currentYear = new Date().getFullYear();

const footerColumns = computed<FooterColumn[]>(() => {
  if (props.footerType === 'auth') {
    return [...authFooterItems]
  }
  const base = isCompaniesHost ? defaultFooterItems : [jobsAndCareerColumn, ...defaultFooterItems]
  return [...base, wad]
})

const authLinks = computed(() =>
    authFooterItems.flatMap(col => col.items)
)

</script>

<template>
  <footer class="wad4-footer" :class="{ 'wad4-footer--auth': props.footerType === 'auth' }">

    <div v-if="props.footerType === 'auth'" class="wad4-footer--auth-container">
      <div class="wad4-footer--auth-container__social">
        <a
            v-for="(item, idx) in social.items"
            :key="idx"
            :href="item.url"
            class="wad4-footer--auth-container__social-link"
            :aria-label="item.text"
            target="_blank"
            rel="noopener noreferrer"
        >
          <span v-html="item.icon" aria-hidden="true"></span>
        </a>
      </div>

      <span class="wad4-footer--auth-container__copyright">
      <span aria-hidden="true">©</span>
      <time :datetime="`${currentYear}-01-01`">{{ currentYear }}</time>
      WeAreDevelopers. All rights reserved
  </span>

      <div class="wad4-footer--auth-container__links">
        <a
            v-for="(item, idx) in authLinks"
            :key="idx"
            :href="item.url"
            class="wad4-footer--auth-container__link"
            :aria-label="item.ariaLabel || item.text"
            :target="item.external ? '_blank' : null"
            :rel="item.external ? 'noopener noreferrer' : null"
        >
          {{ item.text }}
        </a>
      </div>
    </div>

    <nav v-else class="wad4__container wad4__container--columns wad4-footer__columns" aria-label="Footer navigation">
      <section
          v-for="(column, colIdx) in footerColumns"
          :key="colIdx"
          class="wad4-footer__column"
          :aria-labelledby="`footer-col-${colIdx}`"
      >
        <h2 :id="`footer-col-${colIdx}`" class="wad4-footer__column-header">{{ column.header }}</h2>
        <a
            v-for="(item, itemIdx) in column.items"
            :key="itemIdx"
            :href="item.url"
            class="wad4-footer__link"
            :aria-label="item.ariaLabel || item.text"
            :target="item.external ? '_blank' : null"
            :rel="item.external ? 'noopener noreferrer' : null"
        >
          {{ item.text }}
        </a>
      </section>
      <div  class="wad4-footer--auth-container">
        <div class="wad4-footer--auth-container__social">
          <a
              v-for="(item, idx) in social.items"
              :key="idx"
              :href="item.url"
              class="wad4-footer--auth-container__social-link"
              :aria-label="item.text"
              target="_blank"
              rel="noopener noreferrer"
          >
            <span v-html="item.icon" aria-hidden="true"></span>
          </a>
        </div>

        <span class="wad4-footer--auth-container__copyright">
      <span aria-hidden="true">©</span>
      <time :datetime="`${currentYear}-01-01`">{{ currentYear }}</time>
      WeAreDevelopers. All rights reserved
  </span>

        <div class="wad4-footer--auth-container__links">
          <a
              v-for="(item, idx) in authLinks"
              :key="idx"
              :href="item.url"
              class="wad4-footer--auth-container__link"
              :aria-label="item.ariaLabel || item.text"
              :target="item.external ? '_blank' : null"
              :rel="item.external ? 'noopener noreferrer' : null"
          >
            {{ item.text }}
          </a>
        </div>
      </div>
    </nav>

  </footer>
</template>

<style lang="scss">
.wad4-footer {
  width: 100%;
  font-family: $wad4-default-font-sans;
  color: $wad4-c-text-text-primary;
  background: $wad4-c-background-bg-primary;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  padding-top: $wad4-viewport-section-padding;
  padding-bottom: $wad4-viewport-section-padding;
  row-gap: $wad4-spacing-4xl;
  border-top: 2px solid $wad4-c-border-border-subtle;

  &--auth {
    border-top: none;
    padding: unset;
  }

  &__columns.wad4__container--columns {
    row-gap: $wad4-spacing-4xl;
    padding-bottom: $wad4-spacing-4xl;
  }

  &__column {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: $wad4-spacing-xl;
    flex: 1 0 0;
    flex-wrap: wrap;
    min-width: $wad4-col-2;
    max-width: $wad4-col-3;
  }

  &__column-header {
    font-weight: $wad4-font-weight-regular-bold;
    font-size: 0.875rem;
    line-height: 1.25rem;
    margin: 0;
    color: $wad4-c-text-text-tertiery;
  }

  &__link {
    color: $wad4-c-text-text-primary !important;
    text-decoration: none;
    font-weight: $wad4-font-weight-regular;
    font-size: 0.875rem;
    line-height: 1.25rem;

    display: inline-flex;
    align-items: center;
    gap: $wad4-spacing-sm;

    svg {
      color: $wad4-c-foreground-fg-tertiary;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  &__copyright {
    text-align: center;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: $wad4-c-text-text-primary;
  }

  &--auth-container {
    display: flex;
    align-items: center;
    width: 100%;
    gap: $wad4-spacing-3xl;

    @media (max-width: $wad4-breakpoint-tablet-max) {
        flex-direction: column;
        align-items: center;
        gap: $wad4-spacing-lg;

    }

    &__social {
      display: flex;
      gap: $wad4-spacing-lg;
      align-items: center;
    }

    &__social-link {
      background-color: $wad4-c-background-bg-primary;

      svg {
        color: $wad4-c-foreground-fg-tertiary;
        vertical-align: middle
      }
    }

    &__copyright {
      text-align: center;
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: $wad4-c-text-text-primary;
    }

    &__links {
      display: flex;
      gap: $wad4-spacing-3xl;
      font-size:0.875rem;
      line-height: 1.25rem;
    }

    &__link {
      font-size: 0.875rem;
      color: $wad4-c-text-text-primary;
      text-decoration: none;

      &:hover{
        text-decoration: underline;
      }
    }
  }
}
</style>
